'use client';

import {
    A11Y_FILTER_DESCRIBED_BY,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
} from '../../SearchResultsFacets/constants';

import { StyledList } from '@/components/SearchResultsFacets/styles';
import { checkRefinementApplied } from '@/components/SearchResultsFacets/utils';
import { BOPIM_PICK_UP_TEXT_KEY, PICK_UP_TEXT_KEY } from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    FILTER,
    FILTER_PICKUP,
    FILTER_REMOVE,
    FILTER_TYPE_LOCATION,
    FILTER_VAL,
    SHOP_BY_PREFIX,
    TRACK_TYPE_MULTI_ATTR,
} from '@/src/components/Analytics/constants';
import { FacetNormalized } from '@/src/components/SearchResultsFacets/types';
import useRefinement from '@/src/hooks/useRefinement';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Checkbox } from '@nxt-costco-com/forge-components';
import {
    Analytics,
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

export function DefaultFilterView({
    facet,
    facetConfig,
    resultsText,
    searchService,
}: {
    facet: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    resultsText: string;
    searchService: SearchServiceProps;
}) {
    const { handleDoRefine } = useRefinement({ searchService, facetConfig });
    const { queryParams: searchParams } = useQueryParams();

    const showMoreText = getKeyValue('facet.showmoretext', facetConfig);
    return (
        <StyledList
            variant="ExpandableList"
            showMoreText={showMoreText}
            ariaLabeledById={facet.facetKey}
            buttonId={`show_more_${facet.label.toLowerCase().split(' ').join('_')}`}
        >
            {facet.buckets
                .filter(bucket => {
                    return !(
                        facet.facetKey === LW_BOPIW_FACET_KEY &&
                        bucket.val === false
                    );
                })
                .map(bucket => {
                    let label = bucket.val;
                    // BOPIW comes in as { val: true, count: <count> }
                    if (
                        facet.facetKey === LW_BOPIW_FACET_KEY &&
                        bucket.val === true
                    ) {
                        label = getKeyValue(PICK_UP_TEXT_KEY, facetConfig)!;
                    }

                    if (
                        facet.facetKey === LW_BOPIM_FACET_KEY &&
                        bucket.val === true
                    ) {
                        label = getKeyValue(
                            BOPIM_PICK_UP_TEXT_KEY,
                            facetConfig,
                        )!;
                    }

                    const isChecked = checkRefinementApplied({
                        facet,
                        bucket,
                        searchParams,
                        searchService,
                        facetConfig,
                    });
                    return (
                        <Box
                            key={bucket?.val?.toString()}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                            data-private={bucket?.leads_to_fsa_chdi}
                            data-bi-private={bucket?.leads_to_fsa_chdi}
                        >
                            <Analytics
                                analyticData={{
                                    trackedData: [
                                        {
                                            key: isChecked
                                                ? FILTER_REMOVE
                                                : FILTER,
                                            value:
                                                facet.label === FILTER_PICKUP
                                                    ? FILTER_TYPE_LOCATION
                                                    : SHOP_BY_PREFIX +
                                                      facet.label,
                                        },
                                        {
                                            key: FILTER_VAL,
                                            value: label?.toString(),
                                        },
                                    ],
                                    trackType: TRACK_TYPE_MULTI_ATTR,
                                    doNotTrack: bucket?.leads_to_fsa_chdi,
                                }}
                            >
                                <Checkbox
                                    ariaDescribeText={A11Y_FILTER_DESCRIBED_BY}
                                    onChange={event =>
                                        handleDoRefine(
                                            facet,
                                            bucket,
                                            event?.target?.checked,
                                        )
                                    }
                                    isChecked={!!isChecked}
                                    label={
                                        (
                                            <>
                                                {label}{' '}
                                                <Box sx={visuallyHidden}>
                                                    {`${bucket.count?.toLocaleString()} ${resultsText}`}
                                                </Box>
                                            </>
                                        ) as unknown as string
                                    }
                                    id={`${facet.facetKey}-${bucket.val}`}
                                />
                            </Analytics>
                            <Box aria-hidden>
                                ({bucket.count?.toLocaleString()})
                            </Box>
                        </Box>
                    );
                })}
        </StyledList>
    );
}
